import React, { useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import { IgButton, IgTypography } from "@intelligems/ig-theme";
import { ExperienceListDto } from "@intelligems/ig-types";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from "@mui/material";
import { useGetExperiencesList } from "@src/api/experiences-list/get-experiences-list";
import { EXPERIMENT_ROUTES, PERSONALIZATION_ROUTES } from "@src/routes/routes";
import { setListFilter } from "@src/store/slices/appSettings";
import { isDialogOpen, setDialog } from "@src/store/slices/global";
import { contactSupport } from "@src/utils/contactSupport";

export const OfferLimitReachedDialog = () => {
  const navigate = useNavigate();
  const isOpen = useSelector(isDialogOpen("offerLimitReached"));
  const dispatch = useDispatch();
  const experiences = useGetExperiencesList({ category: "experiment" });
  const personalizations = useGetExperiencesList({
    category: "personalization",
  });

  const numberOfOfferPersonalizations = useMemo(() => {
    return (
      personalizations?.data?.experiencesList?.filter(
        (personalization: ExperienceListDto) => {
          return (
            personalization.testTypes.hasTestCampaign &&
            personalization.archivedAtTs === null
          );
        }
      ).length || 0
    );
  }, [personalizations?.data?.experiencesList]);

  const totalOffers = useMemo(() => {
    let total = 0;

    experiences?.data?.experiencesList.forEach((experience) => {
      if (Array.isArray(experience.variations)) {
        experience.variations.forEach((variation) => {
          if (variation.offerId) {
            total += 1;
          }
        });
      }
    });

    return total;
  }, [experiences?.data?.experiencesList]);

  const handleClose = () => {
    dispatch(setDialog({ dialog: "offerLimitReached", open: false }));
  };

  return (
    <Dialog
      open={isOpen}
      onClose={handleClose}
      PaperProps={{
        sx: { borderRadius: "5px", padding: "10px", maxWidth: "500px" },
      }}
    >
      <DialogTitle>Offer Limit Reached</DialogTitle>
      <DialogContent sx={{ paddingBottom: "10px" }}>
        <IgTypography>
          You’ve reached the maximum limit of{" "}
          <IgTypography sx={{ fontWeight: 600 }}>15</IgTypography> Offers across
          your Experiences and tests. In order to create a new Offer Experiences
          or Offer Tests, you’ll need to:
        </IgTypography>
        <ul>
          {numberOfOfferPersonalizations > 0 && (
            <li>
              Archive or delete one of your{" "}
              <IgTypography
                onClick={() => {
                  dispatch(
                    setDialog({ dialog: "offerLimitReached", open: false })
                  );
                  void navigate(`${PERSONALIZATION_ROUTES.Personalizations}`);
                  dispatch(
                    setListFilter({
                      type: "personalizationListFilters",
                      key: "testTypes",
                      value: ["hasTestCampaign"],
                    })
                  );
                }}
                sx={{ cursor: "pointer", fontWeight: 600 }}
              >
                {numberOfOfferPersonalizations} Offer Experiences
              </IgTypography>
              .
            </li>
          )}
          <Box my={2} />
          {totalOffers > 0 && (
            <li>
              End or delete one of your{" "}
              <IgTypography
                onClick={() => {
                  dispatch(
                    setDialog({ dialog: "createExperiment", open: false })
                  );
                  dispatch(
                    setDialog({ dialog: "offerLimitReached", open: false })
                  );
                  void navigate(`${EXPERIMENT_ROUTES.Experiments}`);

                  dispatch(
                    setListFilter({
                      type: "experienceListFilters",
                      key: "testTypes",
                      value: ["offer"],
                    })
                  );
                }}
                sx={{ cursor: "pointer", fontWeight: 600 }}
              >
                Offer Tests
              </IgTypography>
              , which contain {totalOffers} offers across their groups.
            </li>
          )}
        </ul>
        <Box my={2}>
          <IgTypography>
            You can also{" "}
            <IgTypography
              onClick={() => contactSupport()}
              sx={{ cursor: "pointer" }}
            >
              reach out to support
            </IgTypography>{" "}
            to raise the limit.
          </IgTypography>
        </Box>
      </DialogContent>
      <DialogActions sx={{ marginRight: "10px" }}>
        <IgButton onClick={handleClose}>OK</IgButton>
      </DialogActions>
    </Dialog>
  );
};
